// 3rd party libs
import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { skipToken } from '@reduxjs/toolkit/query';

// Components
import Scope from 'pages/UserManagementTwo/components/Group/AddEditGroup/Scope';
import Permission from 'pages/UserManagementTwo/components/Group/AddEditGroup/Permission';

import GroupInfo from 'pages/UserManagementTwo/components/Group/AddEditGroup/GroupInfo';
import { DataRenderer } from 'components/machine-health';

// Types
import { BUOrganization, BusinessUnit, Id } from 'types';
import {
  GroupItem,
  GroupOrganization,
  GroupScope,
  UserFormElement,
  UserTypes
} from 'types/user-management';
import { User } from 'types/user-management';
import {
  useGetCountByBusinessUnitsQuery,
  useGetLinesQuery,
  useGetOrganizationsQuery,
  useGetPlantsQuery,
  useGetUserManagementGroupByIdV2Query,
  useLazyGetUserManagementV2Query
} from 'api';
import { GroupModalFooter } from 'pages/UserManagementTwo/components/index';
import { getScopeCounts } from 'pages/UserManagement/components/Group/AddEditGroup/Scope/ScopeUtils';
import { isAlphaNumeric } from 'common/helpers/v1';
import Collapse from 'rc-collapse';
import {
  MenuPanel,
  expandIcon
} from 'pages/MaintenanceServiceDashboard/MaintenanceQueryTableByFrequency';
import GroupUsers from './GroupUsers/Index';
import theme from 'themes';
import { debounce } from 'lodash';

interface Props {
  id?: Id;
  setIsChanged: (isChanged: boolean) => void;
  onClose: () => void;
  onCloseVerifyData: () => void;
  initialGroupInfo: GroupItem;
  setGroupName?: (groupName: string) => void;
  groupName?: string;
  isChanged?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 0.5rem;
  height: 91.5%;
  overflow-y: scroll;
`;

const FormContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0 1rem 0;

  .group-collapse-panel > .rc-collapse-item {
    padding: 1.125rem 0;
    border-bottom: 0.063rem solid ${theme.colors.lightGrey4};

    &:first-child {
      padding-top: 0;
    }
    &:last-child {
      border: none;
    }
  }
  border-bottom: 0.063rem solid ${theme.colors.lightGrey4};
`;
export const Header = styled.div`
  font-family: Roboto;
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  padding-left: 1rem;
`;
export const MandatoryIndicator = styled.div`
  color: #b62c10;
  padding-left: 5px;
`;
export const HeaderWrapper = styled.div`
  display: flex;
`;
const UserCount = styled.div`
color: color: ${({ theme }) => theme.colors.text.lightBlack};
font-family: Roboto;
font-size: 1rem;
font-style: normal;
font-weight: 600;
line-height: normal;
margin-left: 0.25rem;
`;
export const getUserToFormElement = (users: User[]): UserFormElement[] => {
  return users.map((u) => {
    return { id: u.id, validationState: {}, user: u } as UserFormElement;
  });
};
const updateSelectedBUsInEditMode = (
  BUs: BusinessUnit[],
  selectedBus: BusinessUnit[]
): BusinessUnit[] => {
  const selectedList: BusinessUnit[] = [];
  selectedBus.forEach((bu) => {
    const unit = BUs.find((b) => b.id === bu.id);
    if (unit) {
      selectedList.push(unit);
    } else {
      selectedList.push(bu);
    }
  });
  return selectedList;
};
const getSelectedBUsCountInEditMode = (
  BUs: BusinessUnit[],
  selectedBus: BusinessUnit[]
): { locations: number; lines: number; machines: number } => {
  const counts = { locations: 0, lines: 0, machines: 0 };
  selectedBus.forEach((item) => {
    const unit = BUs?.find((bu) => bu.id === item.id);
    if (unit) {
      counts.locations += unit?.organizationCount as number;
      counts.lines += unit?.plantCount as number;
      counts.machines += unit?.machineCount as number;
    }
  });
  return counts;
};
const AddEditGroup = ({
  id,
  initialGroupInfo,
  setIsChanged,
  onClose,
  onCloseVerifyData,
  setGroupName,
  groupName,
  isChanged
}: Props): ReactElement => {
  const { data: allOrganizations, isFetching: isLoadingOrganizations } = useGetOrganizationsQuery();
  const { data: allPlants, isFetching: isLoadingPlants } = useGetPlantsQuery();
  const { data: lines, isFetching: isLoadingLines } = useGetLinesQuery({ plantIds: [] });
  const {
    data: groupData,
    error: groupError,
    isLoading: isLoadingGroup
  } = useGetUserManagementGroupByIdV2Query(id ?? skipToken);
  const { data: businessUnits, isLoading: bizUnitLoading } = useGetCountByBusinessUnitsQuery();
  const getPanels = [
    { title: 'Scope', isRequire: true },
    { title: 'Permissions', isRequire: true },
    { title: 'User', isRequire: false }
  ];
  const [isVisibleScopeDetail, setIsVisibleScopeDetail] = useState(false);
  const initialGroupData = groupData ?? initialGroupInfo;
  const [groupInfoState, setGroupInfoState] = useState<GroupItem>(initialGroupData);
  const [getGroupList] = useLazyGetUserManagementV2Query();
  const lastCheckedName = useRef('');

  const validateUniqueGroupName = useCallback(
    debounce(async (name) => {
      // Prevent repeat calls for while saving the group name
      if (lastCheckedName.current === name) return;
      lastCheckedName.current = name;

      try {
        const groups = await getGroupList({
          type: 'group',
          nameSearchQuery: name.trim(),
          limit: 1,
          offset: 0
        });
        const isGroupNameExist = groups?.data?.items?.find(
          (item) => item?.name?.toLowerCase() === name?.toLowerCase()
        );
        if (isGroupNameExist) {
          const errorObj = {
            name: 'Group name already exists'
          };
          setGroupInfoState((groupInfoState) => ({ ...groupInfoState, validationError: errorObj }));
        } else {
          setGroupInfoState((groupInfoState) => ({
            ...groupInfoState,
            validationError: undefined
          }));
        }
      } catch (error) {
        console.error('Error fetching group list:', error);
      }
    }, 200), // Adjust debounce delay
    []
  );

  const [activePanelKeys, setActivePanelKeys] = useState<React.Key[]>([
    'Scope',
    'Permission',
    'User'
  ]);
  const [groupUsers, setGroupUsers] = useState<UserFormElement[]>([]);
  const [scopeState, setScopeState] = useState<GroupScope>({
    allOrganizations: false,
    allPlants: false,
    allMachines: false,
    organizations: [],
    plants: [],
    machines: []
  });
  const [businessUnitsCount, setBusinessUnitsCount] = useState({
    locations: 0,
    lines: 0,
    machines: 0
  });
  const [selectedBUs, setSelectedBUs] = useState<BusinessUnit[]>([]);
  const [buSelectionVisible, setBuSelectionVisible] = useState<boolean>(false);
  const [existingGroupUser, setExistingGroupUser] = useState<UserTypes[]>([]);
  const groupDataScope = {
    organizations: groupData?.organizations ?? [],
    plants: groupData?.plants ?? [],
    machines: groupData?.machines ?? [],
    allOrganizations: groupData?.allOrganizations ?? false,
    allPlants: groupData?.allPlants ?? false,
    allMachines: groupData?.allMachines ?? false
  };

  const [counts, setCounts] = useState({ organizations: '0', plants: '0', machines: '0' });

  useEffect(() => {
    setGroupInfoState(groupData ?? initialGroupInfo);
    if (id) {
      setScopeState(
        groupData
          ? { ...scopeState, organizations: groupData.organizations as GroupOrganization[] }
          : { ...scopeState }
      );
      setGroupUsers(
        groupData?.users ? getUserToFormElement(groupData?.users as User[]) : groupUsers
      );
    }
    if (groupData) {
      if (groupData.isInternal && businessUnits) {
        setSelectedBUs(
          updateSelectedBUsInEditMode(businessUnits, groupData.businessUnits as BusinessUnit[])
        );
        setBuSelectionVisible(true);
        setBusinessUnitsCount(
          getSelectedBUsCountInEditMode(businessUnits, groupData.businessUnits as BusinessUnit[])
        );
      } else {
        setIsVisibleScopeDetail(true);
        setCounts(getScopeCounts(allOrganizations ?? [], allPlants ?? [], groupDataScope));
      }
      setGroupName?.(groupData.name);
    }
  }, [groupData, businessUnits]);

  const isLoadingAddEditGroup = id
    ? isLoadingGroup || isLoadingOrganizations || isLoadingPlants || isLoadingLines
    : isLoadingOrganizations || isLoadingPlants || isLoadingLines;

  const onChangeAccessGroup = (isInternal: boolean) => {
    // When access Group changes, set scope detail to initial
    const scope = {
      organizations: [],
      plants: [],
      machines: [],
      allOrganizations: false,
      allPlants: false,
      allMachines: false
    };
    setCounts({ organizations: '0', plants: '0', machines: '0' });
    setGroupInfoState({
      ...scope,
      id: id,
      isInternal: isInternal,
      name: groupInfoState.name,
      permissions: groupInfoState.permissions,
      users: groupInfoState.users
    });
    setBuSelectionVisible(false);
    setSelectedBUs([]);
    setBusinessUnitsCount({ locations: 0, lines: 0, machines: 0 });
    setIsVisibleScopeDetail(false);
  };
  const updateGroupData = (userElements: UserFormElement[]): void => {
    if (id) setIsChanged(true);
    setGroupUsers([...userElements]);
    setGroupInfoState({
      ...groupInfoState,
      users: userElements.map((e) => e.user)
    });
  };
  const setBUsAndUpdateInfoState = (BUs: BusinessUnit[]): void => {
    if (id) setIsChanged(true);
    setSelectedBUs([...BUs]);
    setGroupInfoState({
      ...groupInfoState,
      businessUnits: getFilteredBUs(BUs),
      organizations: getFilteredOrgs(BUs)
    });
  };
  const getFilteredBUs = (BUs: BusinessUnit[]): { id: number }[] => {
    return BUs.map((b) => {
      return { id: b.id };
    });
  };
  const getFilteredOrgs = (BUs: BusinessUnit[]): GroupOrganization[] => {
    let BUOrgs: BUOrganization[] = [];
    BUs.forEach((b) => {
      const v = b.organizations as BUOrganization[];
      BUOrgs = [...BUOrgs, ...v];
    });
    return BUOrgs.map((b) => {
      const org: GroupOrganization = {
        organizationId: '',
        value: '',
        label: '',
        allPlants: true,
        allMachines: true,
        plants: [],
        machines: [],
        selectedPlants: []
      };
      return { ...org, organizationId: b.id, value: b.id, label: b.name };
    });
  };

  const handleAddExistingUser = (users: UserTypes[]) => {
    setIsChanged(!!users.length);
    setExistingGroupUser(users);
    setGroupInfoState({ ...groupInfoState, existingUsers: users });
  };
  const hasSingleTrailingSpaceOrValidPattern = (str: string): boolean => {
    // Check if the string is empty
    if (str === '') {
      return true;
    }

    // Check if the string starts with an alphabet
    if (!/^[a-zA-Z]/.test(str)) {
      return false;
    }

    // Check if the string is alphanumeric (with allowed spaces and hyphens and underscores)
    if (!/^[a-zA-Z0-9 _-]+$/.test(str)) {
      return false;
    }

    // Check for consecutive hyphens or spaces
    if (/(--)|( {2})/.test(str)) {
      return false;
    }
    // Check for consecutive underscores or spaces
    if (/(__)|( {2})/.test(str)) {
      return false;
    }

    // Check for space just before or after a hyphen
    if (/ -|-\s/.test(str)) {
      return false;
    }
    // Check for space just before or after an underscore
    if (/ _|_\s/.test(str)) {
      return false;
    }

    // Check for hyphen followed by underscore or underscore followed by hyphen
    if (/-_|_-/.test(str)) {
      return false;
    }
    // If all checks passed
    return true;
  };
  return (
    <Container>
      <FormContainer>
        <DataRenderer
          isLoading={isLoadingAddEditGroup}
          error={id && groupError ? 'Failed to Load' : ''}
        >
          <GroupInfo
            groupInformation={{
              name: groupInfoState.name,
              isInternal: groupInfoState.isInternal as boolean,
              validation: groupInfoState.validationError?.name
            }}
            onGroupNameChange={(newGroupName: string) => {
              (isAlphaNumeric(newGroupName) ||
                newGroupName.trimStart() === '' ||
                hasSingleTrailingSpaceOrValidPattern(newGroupName)) &&
                setGroupInfoState({
                  ...groupInfoState,
                  name: newGroupName.trimStart()
                });
              newGroupName &&
                newGroupName !== groupName &&
                validateUniqueGroupName(newGroupName.trimStart());
              if (newGroupName !== groupName) {
                setIsChanged(true);
              } else {
                setIsChanged(false);
              }
            }}
            onGroupAccessChange={(isInternal: boolean) => {
              onChangeAccessGroup(isInternal);
              if (!isInternal) setScopeState({ ...scopeState, organizations: [] });
              setIsChanged(true);
            }}
          />
          <Collapse
            activeKey={activePanelKeys}
            onChange={(key: React.Key | React.Key[]) =>
              setActivePanelKeys(Array.isArray(key) ? key : [key])
            }
            destroyInactivePanel={true}
            expandIcon={expandIcon}
            className="group-collapse-panel"
          >
            <>
              {getPanels.map((panel) => {
                const header = (
                  <>
                    <HeaderWrapper>
                      <Header>{panel.title}</Header>
                      {panel.isRequire && <MandatoryIndicator>*</MandatoryIndicator>}
                      {panel.title === 'User' ? (
                        <UserCount>({groupUsers.length + existingGroupUser.length})</UserCount>
                      ) : (
                        <></>
                      )}
                    </HeaderWrapper>
                  </>
                );
                return (
                  <MenuPanel
                    showArrow={true}
                    collapsible="header"
                    key={panel.title}
                    header={header}
                    mb={0}
                    style={{ marginBottom: 0 }}
                  >
                    {panel.title === 'Scope' ? (
                      <Scope
                        organizations={allOrganizations}
                        plants={allPlants}
                        lines={lines}
                        counts={{
                          organizations: counts.organizations,
                          plants: counts.plants,
                          machines: counts.machines
                        }}
                        setCounts={setCounts}
                        setGroupInfoState={setGroupInfoState}
                        groupInfoState={groupInfoState}
                        setIsChanged={setIsChanged}
                        setIsVisibleScopeDetail={setIsVisibleScopeDetail}
                        isVisibleScopeDetail={isVisibleScopeDetail}
                        setScopeState={setScopeState}
                        scopeState={scopeState as GroupScope}
                        businessUnits={!bizUnitLoading ? businessUnits : []}
                        selectedBUs={selectedBUs}
                        setSelectedBUs={setBUsAndUpdateInfoState}
                        businessUnitsCount={businessUnitsCount}
                        setBusinessUnitsCount={setBusinessUnitsCount}
                        buSelectionVisible={buSelectionVisible}
                        setBuSelectionVisible={setBuSelectionVisible}
                      />
                    ) : panel.title === 'Permissions' ? (
                      <Permission
                        groupPermissions={groupInfoState.permissions ?? []}
                        onPermissionChange={(permissions) => {
                          setGroupInfoState({ ...groupInfoState, permissions: permissions });
                          setIsChanged(true);
                        }}
                      />
                    ) : (
                      <GroupUsers
                        groupId={id as string}
                        groupUsers={groupUsers}
                        setGroupUsers={updateGroupData}
                        existingGroupUsers={existingGroupUser}
                        handleAddExistingUser={handleAddExistingUser}
                      />
                    )}
                  </MenuPanel>
                );
              })}
            </>
          </Collapse>
        </DataRenderer>
      </FormContainer>

      <GroupModalFooter
        tableItemId={id}
        onClose={onClose}
        onCloseVerifyData={onCloseVerifyData}
        groupInfo={groupInfoState}
        isChanged={isChanged}
      />
    </Container>
  );
};

export default AddEditGroup;
